// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, ProductSubscriptionDto, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDeliveryScheduleButton from "@insite/content-library/Components/ProductDeliveryScheduleButton";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CartLineContainer from "@plumberssupply/App/Components/Cart/CartContainerLayout";
import { cartLineBreakpoints } from "@plumberssupply/App/Components/Cart/CartContainerLayout";
import Notify from "@plumberssupply/App/Components/Notify";
import ProductPreviousPurchase from "@plumberssupply/App/Components/Product/ProductPreviousPurchase";
import RoundToBox, { RoundToBoxStyles } from "@plumberssupply/App/Components/Product/RoundToBox";
import cartLineSharedStyles from "@plumberssupply/App/SharedStyle/Cart/CartLineSharedStyles";
import { getRoundToBox, getShowRoundToBox } from "@plumberssupply/App/Store/Selectors/CustomProductDetailsSelectors";
import { getScratchAndDent } from "@plumberssupply/App/Store/Selectors/ScratchAndDentSelectors";
import ProductAvailability, {
    ProductAvailabilityStyles,
} from "@plumberssupply/Overrides/Components/ProductAvailability";
import ProductPrice, { ProductPriceStyles } from "@plumberssupply/Overrides/Components/ProductPrice";
import CartLineQuantity, { CartLineQuantityStyles } from "@plumberssupply/Overrides/Widgets/Cart/CartLineQuantity";
import IconCaretDown from "@plumberssupply/XDL/Icons/IconCaretDown";
import IconCaretUp from "@plumberssupply/XDL/Icons/IconCaretUp";
import { ellipsis, maxLengthAttr, productGlobalInventory, xScreen } from "@plumberssupply/XDL/Utilities";
import { mergeWithCss } from "@plumberssupply/XDL/Utilities/mergeWithCss";
import React, { FC, useContext, useState } from "react";
import { useEffect } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    productSettings?: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    updateCartLine?: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine?: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;
    // XNG-Changes
    editable?: boolean;
    showInvalidPriceMessage?: boolean;
    showSavingsPercent?: boolean;
    showSavingsAmount?: boolean;
    editableCostCode?: boolean;
    warehouseTransfer?: boolean;
    hideRTB?: boolean;
    hidePriceForEmployee?: boolean;
    isNoteDisabled?: boolean;
    isExpressCheckout?: boolean;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    // XNG-Change
    isEmployeeWithoutPricing: state.context.session.userRoles?.includes("EmployeeWithoutPricing"),
    cartBillTo: getBillToState(state, ownProps.cart.billToId).value,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    container?: InjectableCss;
    productImageGridItem?: InjectableCss;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: InjectableCss;
    cartLineInfoContainer?: InjectableCss;
    productInfoGridItem?: InjectableCss;
    productInfoContainer?: InjectableCss;
    productBrandAndDescriptionGridItem?: InjectableCss;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: InjectableCss;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: InjectableCss;
    configurationOptionText?: TypographyProps;
    configurationOptionLabelText?: TypographyProps;
    productPartNumbersGridItem?: InjectableCss;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: InjectableCss;
    quantityAndExtendedUnitNetPriceContainer?: InjectableCss;
    quantityGridItem?: InjectableCss;
    quantityRoundToBox?: InjectableCss;
    roundToBox?: RoundToBoxStyles;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: InjectableCss;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: InjectableCss;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItem?: InjectableCss;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: InjectableCss;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    addLineNotes?: LinkPresentationProps;
    removeCartLineGridItem?: InjectableCss;
    removeCartLineLink?: LinkPresentationProps;
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    roundToBoxTooltip?: TooltipPresentationProps;
    configurationOptionsWrapper?: InjectableCss;
    promotionsAndScheduleWrapper?: InjectableCss;
    productPreviousPurchaseStyles?: TypographyPresentationProps;
    // XNG-Change
    productInfoWrappers?: InjectableCss;
    warningIcon?: IconPresentationProps;
    stockWarningMessage?: TypographyProps;
    qtyOrderedAndShipped?: TypographyProps;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    productImage: {
        ...cartLineSharedStyles.productImage,
    },
    productBrandAndDescriptionGridItem: {
        ...cartLineSharedStyles.productBrandAndDescriptionGridItem,
    },
    productPartNumbersGridItem: {
        ...cartLineSharedStyles.productPartNumbersGridItem,
    },
    productPartNumbers: {
        container: {
            css: css`
                ${xScreen("gte", cartLineBreakpoints.md)} {
                    max-width: 177px;
                    width: 177px;
                }
            `,
        },
        manufacturerItemValueText: {
            css: css`
                ${xScreen("gte", cartLineBreakpoints.md)} {
                    ${ellipsis(1)}
                }
            `,
        },
        erpNumberValueText: {
            css: css`
                ${xScreen("gte", cartLineBreakpoints.md)} {
                    ${ellipsis(1)}
                }
            `,
        },
    },
    productDescription: {
        ...cartLineSharedStyles.productDescription,
    },
    quantityRoundToBox: {
        ...cartLineSharedStyles.quantityRoundToBox,
    },
    roundToBox: {
        ...cartLineSharedStyles.roundToBox,
    },
    addLineNotes: {
        ...cartLineSharedStyles.addLineNotes,
    },
    addToListLink: {
        ...cartLineSharedStyles.addToListLink,
    },
    productAvailability: {
        ...cartLineSharedStyles.productAvailability,
        availabilityByWarehouseLink: {
            typographyProps: {
                size: 12,
                css: css`
                    line-height: 15px;
                    ${xScreen("gte", cartLineBreakpoints.md)} {
                        ${ellipsis(1)}
                    }
                `,
            },
        },
    },
    removeCartLineLink: {
        ...cartLineSharedStyles.removeCartLineLink,
    },
    extendedUnitNetPriceText: {
        ...cartLineSharedStyles.extendedUnitNetPriceText,
        css: css`
            @media print {
                margin-left: -10px;
            }
        `,
    },
    productPrice: {
        ...cartLineSharedStyles.productPrice,
    },
    promotionsAndScheduleWrapper: {
        ...cartLineSharedStyles.promotionsAndScheduleWrapper,
    },
    configurationOptionsWrapper: {
        ...cartLineSharedStyles.configurationOptionsWrapper,
    },
    configurationOptionLabelText: {
        ...cartLineSharedStyles.configurationOptionLabelText,
    },
    configurationOptionText: {
        ...cartLineSharedStyles.configurationOptionText,
    },
    promotionNameText: {
        ...cartLineSharedStyles.promotionNameText,
    },
    costCodeLabelText: {
        ...cartLineSharedStyles.costCodeLabelText,
    },
    costCodeText: {
        ...cartLineSharedStyles.costCodeText,
    },
    deliveryScheduleButton: {
        ...cartLineSharedStyles.deliveryScheduleButton,
    },
    productPreviousPurchaseStyles: {
        css: css`
            ${xScreen("gte", cartLineBreakpoints.md)} {
                margin-left: 5px;
            }
        `,
    },
    warningIcon: {
        ...cartLineSharedStyles.warningIcon,
    },
    qtyOrderedAndShipped: {
        ...mergeWithCss(cartLineSharedStyles.qtyOrderedAndShipped, {
            weight: "bold",
        }),
    },
    productInfoWrappers: {
        ...cartLineSharedStyles.productInfoWrappers,
    },
    stockWarningMessage: {
        ...cartLineSharedStyles.qtyOrderedAndShipped,
    },
    binInformationsGridItem: {
        width: 12,
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    showInventoryAvailability,
    showLineNotes,
    showImage = true,
    showProductTitle = true,
    showBrand = true,
    showPerUnitPrice = true,
    showPriceLabel = true,
    showAddToList = true,
    showProductNumber = true,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    showPricing,
    showVmiBinDetails,
    editable = true,
    showSavingsAmount,
    showSavingsPercent,
    showInvalidPriceMessage,
    editableCostCode,
    warehouseTransfer,
    hideRTB,
    hidePriceForEmployee = false,
    isEmployeeWithoutPricing,
    isNoteDisabled,
    cartBillTo,
    isExpressCheckout,
}) => {
    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    // XNG-Changes CartLinesNotes
    const isScratchAndDent = !!getScratchAndDent(cartLine.properties);
    const [showCartLineNotes, setShowCartLineNotes] = useState(isScratchAndDent);
    const [notes, setNotes] = useState(cartLine.notes);
    // XNG-Changes: add isRoundToBox
    const [isRoundToBox, setIsRoundToBox] = useState(getRoundToBox(cartLine.properties));
    const lineNotesClickHandler = () => {
        setShowCartLineNotes(!showCartLineNotes);
    };
    const changeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(event.currentTarget.value);
    };
    const blurHandler = () => {
        notesChangeHandler(notes);
    };

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine?.({ cartLineId: cartLine.id });
            } else {
                updateCartLine?.({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode) {
            updateCartLine?.({
                cartLine: {
                    ...cartLine,
                    costCode,
                },
                shouldNotLoadFullCart: true,
            });
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine?.({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine?.({ cartLineId: cartLine.id });
        setShowConfirmationModal(false);
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
            properties: { plumbers_roundToBox: `${!!isRoundToBox}` },
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }
        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLine?.({
            cartLine: {
                ...cartLine,
                properties: {
                    productSubscription: JSON.stringify(updatedSubscription),
                },
            },
        });
    };

    const handleRoundToBoxChange = (value: boolean) => {
        setIsRoundToBox(value);
        updateCartLine?.({
            cartLine: {
                ...cartLine,
                properties: {
                    ...cartLine.properties,
                    plumbers_roundToBox: value.toString(),
                },
            },
        });
    };

    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);

    const errorMessages: React.ReactNode[] = [];

    if (showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(tooManyRequestedMessage);
    }

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    useEffect(() => {
        if (showLineNotes && notes) {
            setShowCartLineNotes(true);
        }
    }, [notes]);

    if (cartLine.cantBuy) {
        errorMessages.push(translate("View-only product"));
    }

    const [styles] = React.useState(() => mergeWithCss(cartLineCardExpandedStyles, extendedStyles));

    // XNG-Changes
    if (warehouseTransfer) {
        return (
            <CartLineQuantity
                cart={cart}
                editable={editable}
                showLabel={!editable}
                showActions={true}
                onQtyOrderedChange={qtyOrderedChangeHandler}
                extendedStyles={styles.quantity}
            />
        );
    }
    const globalInventory = productGlobalInventory((cartLine.properties.globalInventory as any) || "");

    // <XNG-Changes>
    const qtyOrdered = cartLine.qtyOrdered || 0;
    const qtyOnHand =
        cartLine.qtyOnHand > 0 && cartLine.qtyOnHand >= qtyOrdered
            ? qtyOrdered
            : cartLine.qtyOnHand > 0 && cartLine.qtyOnHand <= qtyOrdered
            ? qtyOrdered - cartLine.qtyOnHand < 1
                ? 1
                : cartLine.qtyOnHand
            : cartLine.qtyOnHand;
    const isNotWholesale =
        !cartBillTo?.properties.plumbers_customerSegment ||
        cartBillTo?.properties.plumbers_customerSegment.toLowerCase() !== "wholesale";
    // </XNG-Changes>

    return (
        <>
            <CartLineContainer
                data-test-selector={`cartline_expanded1_${cartLine.productId}_${cartLine.unitOfMeasure}`}
            >
                <CartLineContainer.Image>
                    {showImage && <ProductImage product={cartLine} extendedStyles={styles.productImage} />}
                    <ProductPreviousPurchase
                        product={cartLine as any}
                        extendedStyles={styles.productPreviousPurchaseStyles}
                    />
                </CartLineContainer.Image>
                <CartLineContainer.Info>
                    <StyledWrapper {...styles.productInfoWrappers} data-test-selector="productInfoWrappers">
                        <StyledWrapper {...styles.productBrandAndDescriptionGridItem}>
                            {showBrand && cartLine.brand && (
                                <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                            )}
                            {showProductTitle && (
                                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.productPartNumbersGridItem}>
                            <ProductPartNumbers
                                productNumber={cartLine.erpNumber}
                                customerProductNumber={cartLine.customerName}
                                manufacturerItem={cartLine.manufacturerItem}
                                extendedStyles={styles.productPartNumbers}
                                showProductNumber={showProductNumber}
                            />
                            {showVmiBinDetails && currentMode === "Vmi" && (
                                <ProductBinNumbers
                                    locationId={cart.vmiLocationId}
                                    binId={cartLine.vmiBinId}
                                    isCondensed={false}
                                    extendedStyles={styles.productBinNumbers}
                                />
                            )}
                            {showInventoryAvailability && !cartLine.quoteRequired && (
                                <ProductAvailability
                                    productId={cartLine.productId!}
                                    availability={cartLine.availability!}
                                    unitOfMeasure={cartLine.unitOfMeasure}
                                    trackInventory={cartLine.trackInventory}
                                    failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                    extendedStyles={styles.productAvailability}
                                    globalInventory={globalInventory}
                                />
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                </CartLineContainer.Info>
                <CartLineContainer.Qty>
                    <CartLineQuantity
                        cart={cart}
                        editable={editable}
                        showLabel={!editable}
                        showActions={true}
                        onQtyOrderedChange={qtyOrderedChangeHandler}
                        extendedStyles={styles.quantity}
                    />
                    {!hideRTB && getShowRoundToBox(cartLine.properties) && (
                        <StyledWrapper {...styles.quantityRoundToBox}>
                            <RoundToBox
                                isChecked={getRoundToBox(cartLine.properties)}
                                sizeVariant="small"
                                toggleHandler={handleRoundToBoxChange}
                                extendedStyles={styles.roundToBox}
                            />
                        </StyledWrapper>
                    )}
                </CartLineContainer.Qty>
                <CartLineContainer.QtyOrderedAndShipped>
                    {(isExpressCheckout || !editable) && qtyOnHand < qtyOrdered && (
                        <Typography {...styles.stockWarningMessage}>
                            <Icon {...styles.warningIcon} />
                            {translate("{0} Item(s) Shipped Out of {1}", `${qtyOnHand}`, `${qtyOrdered}`)}
                        </Typography>
                    )}
                    {(isExpressCheckout || !editable || isOutOfStock(cartLine)) && cartLine.qtyOrdered !== null && (
                        <Typography {...styles.qtyOrderedAndShipped}>
                            {isNotWholesale
                                ? translate("QTY Ordered: {0} / QTY Shipped: {1}", `${qtyOrdered}`, `${qtyOnHand}`)
                                : translate("QTY Ordered: {0}", `${qtyOrdered}`)}
                        </Typography>
                    )}
                </CartLineContainer.QtyOrderedAndShipped>
                <CartLineContainer.Price>
                    {(!hidePriceForEmployee || !isEmployeeWithoutPricing) && (
                        <>
                            {!cart.cartNotPriced && (
                                <ProductPrice
                                    product={cartLine}
                                    currencySymbol={cart.currencySymbol}
                                    showSavings={true}
                                    showSavingsAmount={showSavingsAmount}
                                    showSavingsPercent={showSavingsPercent}
                                    showInvalidPriceMessage={showInvalidPriceMessage}
                                    showListPrice={true}
                                    showLabel={false}
                                    extendedStyles={styles.productPrice}
                                    showUnitOfMeasure={showPerUnitPrice}
                                />
                            )}
                        </>
                    )}
                </CartLineContainer.Price>
                <CartLineContainer.Total>
                    {(!isEmployeeWithoutPricing || !hidePriceForEmployee) && (
                        <>
                            {!cartLine.quoteRequired && !cart.cartNotPriced && cartLine.pricing && showPricing && (
                                <>
                                    <Typography
                                        {...styles.extendedUnitNetPriceText}
                                        data-test-selector="cartline_extendedUnitNetPrice"
                                    >
                                        {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                            ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                            : cartLine.pricing.extendedUnitNetPriceDisplay}
                                    </Typography>
                                    {enableVat && (
                                        <>
                                            <Typography as="p" {...styles.vatLabelText}>
                                                {vatPriceDisplay === "DisplayWithVat" ||
                                                vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                    ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                                    : translate("Ex. VAT")}
                                            </Typography>
                                            {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                <>
                                                    <Typography {...styles.subtotalWithoutVatText}>
                                                        {cartLine.pricing.extendedUnitNetPriceDisplay}
                                                    </Typography>
                                                    <Typography as="p" {...styles.vatLabelText}>
                                                        {translate("Ex. VAT")}
                                                    </Typography>
                                                </>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </CartLineContainer.Total>
                <CartLineContainer.Configurations>
                    {editable && !cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                        <StyledWrapper {...styles.configurationOptionsWrapper}>
                            {cartLine.sectionOptions!.map(option => (
                                <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                    <Typography {...styles.configurationOptionLabelText}>
                                        {option.sectionName}
                                    </Typography>
                                    {option.optionName}
                                </Typography>
                            ))}
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.promotionsAndScheduleWrapper}>
                        {showPricing &&
                            promotions?.map(promotion => (
                                <Typography {...styles.promotionNameText} key={promotion.id}>
                                    {promotion.name}
                                </Typography>
                            ))}
                        {cartLine.isSubscription && cartLine.productSubscription && (
                            <ProductDeliveryScheduleButton
                                subscription={
                                    cartLine.properties.productSubscription
                                        ? JSON.parse(cartLine.properties.productSubscription)
                                        : cartLine.productSubscription
                                }
                                disabled={
                                    !cart.canModifyOrder ||
                                    cartLine.isPromotionItem ||
                                    !cartLine.qtyOrdered ||
                                    cartLine.qtyOrdered <= 0
                                }
                                onSave={saveScheduleHandler}
                                extendedStyles={styles.deliveryScheduleButton}
                            />
                        )}
                    </StyledWrapper>
                    {editableCostCode && cart.showCostCode && !cartLine.isPromotionItem && (
                        <StyledWrapper>
                            <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                            {cart.canEditCostCode ? (
                                <Select
                                    value={cartLine.costCode}
                                    onChange={costCodeChangeHandler}
                                    {...styles.costCodeSelect}
                                >
                                    {cart.costCodes?.map(costCode => (
                                        <option key={costCode.costCode} value={costCode.costCode}>
                                            {costCode.description}
                                        </option>
                                    ))}
                                </Select>
                            ) : (
                                <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                            )}
                        </StyledWrapper>
                    )}
                </CartLineContainer.Configurations>
                <CartLineContainer.PeripheralActions>
                    {showLineNotes &&
                        !cartLine.isPromotionItem &&
                        editable &&
                        !isNoteDisabled &&
                        cart.properties["isPunchout"] === undefined &&
                        cart.canModifyOrder && (
                            <Link
                                {...styles.addLineNotes}
                                onClick={lineNotesClickHandler}
                                data-test-selector="cartline_notesShowHideButton"
                            >
                                {translate("Add Line Note")}
                                <Icon size={8} src={showCartLineNotes ? IconCaretUp : IconCaretDown} />
                            </Link>
                        )}
                    {showAddToList && !hideAddToList && cartLine.canAddToWishlist && !cartLine.isRestricted && (
                        <Link {...styles.addToListLink} onClick={addToListClickHandler}>
                            {translate("Add to List")}
                        </Link>
                    )}
                    {showRemoveAction && (
                        <Link
                            onClick={() => setShowConfirmationModal(true)}
                            {...styles.removeCartLineLink}
                            data-test-selector="cartline_removeLine"
                        >
                            <VisuallyHidden>{translate("Remove")}</VisuallyHidden>
                        </Link>
                    )}
                </CartLineContainer.PeripheralActions>
                <CartLineContainer.LineNotesArea>
                    {(showCartLineNotes || (isNoteDisabled && showLineNotes && notes)) && (
                        <TextArea
                            label={translate("Line Note")}
                            placeholder={translate("Please add any additional information for this product here...")}
                            value={notes}
                            id={`notes-${cartLine.id}`}
                            disabled={!cart.canModifyOrder || isScratchAndDent || !editable || isNoteDisabled}
                            onChange={changeHandler}
                            onBlur={blurHandler}
                            data-test-selector="cartline_notes"
                            // XNG-Change
                            maxLength={maxLengthAttr.smallNote}
                        />
                    )}
                </CartLineContainer.LineNotesArea>
                <CartLineContainer.Errors>
                    {errorMessages.length > 0 && (
                        <StyledWrapper>
                            {errorMessages.map((message, index) => (
                                <Notify
                                    type="danger"
                                    data-test-selector="cartline_errorMessage"
                                    key={`error_${cartLine.id}`}
                                >
                                    {message}
                                </Notify>
                            ))}
                        </StyledWrapper>
                    )}
                </CartLineContainer.Errors>
            </CartLineContainer>

            <TwoButtonModal
                headlineText={translate("Confirm Line Removal")}
                modalIsOpen={showConfirmationModal}
                messageText={siteMessage("Cart_RemoveSingleCartLine")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={() => setShowConfirmationModal(false)}
                onSubmit={() => removeCartLineClickHandler()}
                submitTestSelector="RemoveSingleCartLine"
                dataTestSelector="twoButtonModal_RemoveSingleCartLine"
            ></TwoButtonModal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
